'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, Flame } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Offer {
  id: string;
  banner_text: string;
  button_text: string | null;
  button_url: string | null;
}

export default function OfferBanner() {
  const [offer, setOffer] = useState<Offer | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const supabase = createClient();
        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from('offers')
          .select('id, banner_text, button_text, button_url')
          .eq('is_active', true)
          .or(`start_at.is.null,start_at.lte.${now}`)
          .or(`end_at.is.null,end_at.gte.${now}`)
          .order('display_order', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (!error && data) {
          setOffer(data);
        }
      } catch {
        // No offer to show
      } finally {
        setLoaded(true);
      }
    };
    fetchOffer();
  }, []);

  if (!loaded || !offer || dismissed) return null;

  return (
    <div className="relative z-[60] bg-gradient-to-r from-primary via-accent to-secondary text-white">
      <div className="max-w-screen-2xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Flame size={15} className="flex-shrink-0 text-yellow-300" />
          <p className="text-xs sm:text-sm font-medium truncate">{offer.banner_text}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {offer.button_text && offer.button_url && (
            <Link
              href={offer.button_url}
              className="text-xs font-bold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors duration-150 whitespace-nowrap"
            >
              {offer.button_text}
            </Link>
          )}
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss offer"
            className="p-1 rounded-lg hover:bg-white/20 transition-colors duration-150"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
